import React, { ReactNode } from 'react'

import Box from 'components_new/atoms/Box'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'

import Icon, { IconName } from 'components_new/atoms/Icon'
import { SxProps, useTheme } from '@mui/material'

interface EmptyPlaceholderProps {
  actions?: ReactNode
  background?: boolean
  body?: string
  children?: ReactNode
  height?: number | string
  icon?: IconName
  menu?: ReactNode
  menuSx?: SxProps
  orientation?: 'vertical' | 'horizontal'
  scaleFactor?: number
  size?: 'xsmall' | 'small' | 'medium'
  src?: string
  sx?: SxProps
  title?: ReactNode
}

/**
 * The EmptyPlaceholder component is used
 * to show a placholder text when there
 * is not any items to display.
 *
 * It expands, and fills up, all
 * available space.
 */

const EmptyPlaceholder = React.forwardRef(
  (props: EmptyPlaceholderProps, ref: any) => {
    const { size = 'xsmall' } = props

    const theme = useTheme()

    const getIconSize = () => {
      if (props.scaleFactor) {
        return props.scaleFactor * 40
      }

      switch (size) {
      case 'xsmall':
        return 24
      case 'small':
        return 32
      default: // 'medium'
        return 40
      }
    }

    return (
      <Box
        ref={ref}
        sx={{
          height: props.height ?? '100%',
          display: 'flex',
          flexDirection: props.orientation === 'horizontal' ? 'row' : 'column',
          justifyContent:
            props.orientation === 'horizontal' ? 'flex-start' : 'center',
          alignItems: 'center',
          bgcolor: props.background ? theme.palette.action.hover : undefined,
          borderRadius: props.background
            ? `${theme.shape.borderRadius}px`
            : undefined,
          gap: 1,
          p: 2,
          position: 'relative',
          fontSize: props.scaleFactor ? 16 * props.scaleFactor : undefined,
          ...props.sx
        }}
      >
        {props.icon ? (
          <Icon
            color="disabled"
            name={props.icon}
            sx={{ fontSize: getIconSize() }}
          />
        ) : null}

        {props.src ? (
          <Box
            sx={{
              py: 2,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <img src={props.src} width="60%" />
          </Box>
        ) : null}

        {props.title ? (
          <Text
            align="center"
            color={
              props.body || props.children ? 'text.primary' : 'text.disabled'
            }
            fontWeight={size === 'xsmall' ? undefined : 'bold'}
            variant={
              size === 'medium' ? 'h5' : size === 'small' ? 'h6' : 'body2'
            }
            fontSize={props.scaleFactor ? 'inherit' : undefined}
            sx={{ maxWidth: 'title' }}
          >
            {props.title}
          </Text>
        ) : null}

        {props.body ? (
          <Text
            align="center"
            color={size === 'xsmall' ? 'text.disabled' : 'text.secondary'}
            variant={size === 'xsmall' ? 'body2' : 'body1'}
            fontSize={props.scaleFactor ? 'inherit' : undefined}
          >
            {props.body}
          </Text>
        ) : null}

        {props.children}
        {props.menu ? (
          <Box sx={{ position: 'absolute', top: 8, right: 8, ...props.menuSx }}>
            {props.menu}
          </Box>
        ) : null}

        {props.actions ? (
          <Stack
            alignItems="center"
            sx={{
              gap: 1,
              mt: size === 'medium' ? 2 : size === 'small' ? 1 : undefined
            }}
          >
            {props.actions}
          </Stack>
        ) : null}
      </Box>
    )
  }
)

EmptyPlaceholder.displayName = 'EmptyPlaceholder'

export default EmptyPlaceholder
